import { Link } from 'react-router-dom';
import { AlertTriangle, Clock } from 'lucide-react';

import { useAuth } from '../lib/auth/AuthProvider';
import type { Company } from '../lib/auth/types';
import { graceDaysLeft, isInGrace, isReadOnly } from '../lib/readOnly';
import { isCompanyAdmin } from '../lib/team';
import { t } from '../lib/i18n';

/**
 * Banner global de suscripción — montado en ProtectedLayout (aparece en toda la
 * app). Dos estados visibles:
 *   - GRACIA (access='grace'): franja ÁMBAR. La prueba/pago venció pero la empresa
 *     conserva acceso total por unos días. Empuja a actualizar el pago.
 *   - BLOQUEADA (access='blocked' / solo-lectura): franja ROJA. "Pagar para
 *     desbloquear".
 * Se autooculta si la empresa está operativa (access='active' o trial vigente).
 * El botón de pago es sólo para admin; un MEMBER ve el aviso y el pedido de avisar
 * a un admin. El enforcement real vive en el backend (EnforceTrial).
 */
export function SubscriptionBanner() {
  const { user, company } = useAuth();
  if (!company) return null;

  const inGrace = isInGrace(company);
  const blocked = isReadOnly(company);
  if (!inGrace && !blocked) return null;

  const isAdmin = isCompanyAdmin(user?.role);

  return inGrace ? (
    <GraceBanner company={company} isAdmin={isAdmin} />
  ) : (
    <BlockedBanner isTrial={company.status === 'TRIAL'} isAdmin={isAdmin} />
  );
}

/** Franja ÁMBAR de gracia: días restantes + CTA "Actualizar pago". */
function GraceBanner({ company, isAdmin }: { company: Company; isAdmin: boolean }) {
  const days = graceDaysLeft(company) ?? 0;
  const lastDay = days <= 1;
  const isTrial = company.status === 'TRIAL';

  const title = lastDay
    ? t('subBanner.grace.lastDayTitle')
    : isTrial
      ? t('subBanner.grace.trialTitle')
      : t('subBanner.grace.dueTitle');
  const desc = lastDay
    ? t('subBanner.grace.lastDayDesc')
    : isTrial
      ? t('subBanner.grace.trialDesc', { days })
      : t('subBanner.grace.dueDesc', { days });
  const Icon = lastDay ? AlertTriangle : Clock;

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-token border border-sun/50 bg-sun/10 px-4 py-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sun/20 text-sun">
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-semibold text-ink">{title}</p>
        <p className="text-sm text-muted">{desc}</p>
      </div>
      {isAdmin ? (
        <Link
          to="/facturacion"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-token bg-brand px-4 py-2 text-sm font-semibold text-brand-ink transition-colors hover:bg-brand/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          {t('subBanner.update')}
        </Link>
      ) : (
        <span className="shrink-0 text-xs text-muted">{t('subBanner.askAdmin')}</span>
      )}
    </div>
  );
}

/** Franja ROJA de bloqueo: "Pagar para desbloquear". */
function BlockedBanner({ isTrial, isAdmin }: { isTrial: boolean; isAdmin: boolean }) {
  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-token border border-danger/40 bg-danger/10 px-4 py-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-danger/15 text-danger">
        <AlertTriangle size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-semibold text-ink">
          {isTrial ? t('subBanner.trialTitle') : t('subBanner.blockedTitle')}
        </p>
        <p className="text-sm text-muted">
          {isTrial ? t('subBanner.trialDesc') : t('subBanner.blockedDesc')}
        </p>
      </div>
      {isAdmin ? (
        <Link
          to="/facturacion"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-token bg-brand px-4 py-2 text-sm font-semibold text-brand-ink transition-colors hover:bg-brand/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          {t('subBanner.pay')}
        </Link>
      ) : (
        <span className="shrink-0 text-xs text-muted">{t('subBanner.askAdmin')}</span>
      )}
    </div>
  );
}
